import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivationRecord } from '../models/ActivationRecord';
import { ActivationCodeService } from '../../activation-code.service';
import { PageEvent } from '@angular/material/paginator';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from '../../../../assets/js/vfs_fonts';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DhhpAlertComponent } from 'src/app/shared/dhhpalert/dhhpalert.component';
import { MatDialog } from '@angular/material/dialog';
import { BulkActivationConfirmComponent } from '../bulk-activation-confirm/bulk-activation-confirm.component';

(pdfMake as any).vfs = pdfFonts.vfs;

(pdfMake as any).fonts = {
  OpenSans: {
    normal: 'OpenSans-Regular.ttf',
    bold: 'OpenSans-Bold.ttf',
    italics: 'OpenSans-Italic.ttf'
  }
}

const OUT_FILE_NAME = 'bulk-activation-labels.pdf';

@Component({
  selector: 'app-bulk-activation-table',
  templateUrl: './bulk-activation-table.component.html',
  styleUrls: ['./bulk-activation-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BulkActivationTableComponent implements OnInit {

  data: ActivationRecord[] = [];
  pagedData: ActivationRecord[] = [];
  isDataAvailable: boolean
  isButtonsDisabled: boolean
  savedActivationCodes: boolean

  displayedColumns = ['serialNumber', 'activationCode'];

  pageIndex = 0;
  pageSize = 10;

  constructor(
    private activationCodeService: ActivationCodeService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private dhhpAlert: DhhpAlertComponent,
    private translate: TranslateService,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.subscribeBulkUpload();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagedData();
  }

  private populateActivationRecords(records: ActivationRecord[]) {
    this.data = records;
    this.isDataAvailable = this.data.length > 0;
    this.isButtonsDisabled = !this.isDataAvailable;
    this.pageIndex = 0;
    this.updatePagedData();
    this.cdr.detectChanges();
  }

  updatePagedData() {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.pagedData = this.data.slice(start, end);
  }

  trackByIndex(index: number, _: any): number {
    return index;
  }  

  subscribeBulkUpload() {
    this.activationCodeService.getData().subscribe(data => {
      this.populateActivationRecords(data);
    })
  }

  formatActivationCode(code: string): string {
    if (code == undefined || code == '')
      return ''
    const cleaned = code.replace(/[^a-zA-Z0-9]/g, '').substring(0, 10);
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  generatePdf(cards: ActivationRecord[]): pdfMake.TCreatedPdf {
    const cmToPt = (cm: number) => cm * 28.35;
    const cardWidth = cmToPt(8); //8cm
    const cardHeight = cmToPt(5); //5cm
    const circleRadius = cmToPt(1.1); //1.1cm = 11mm rad

    const content: any[] = [];

    cards.forEach((card, index) => {

      const col = index % 2;
      const row = Math.floor(index % 10 / 2); // 5 rows per page
      const pageIndex = Math.floor(index / 10);
      const marginLeft = cmToPt(2); //2cm
      const marginTop = cmToPt(1.5); //1.5cm
      const x = marginLeft + col * (cardWidth + cmToPt(1));
      const y = marginTop + row * (cardHeight + cmToPt(0.5));

      const cardBlock = {
        absolutePosition: { x, y },
        stack: [
          {
            canvas: [
              //card border
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: cardWidth,
                h: cardHeight,
                lineWidth: 1,
                lineColor: 'black'
              },
              //Red Circle
              {
                type: 'ellipse',
                x: cardWidth / 2 + cmToPt(1.2),
                y: cardHeight / 2,
                r1: circleRadius,
                r2: circleRadius,
                lineColor: 'red',
                lineWidth: 1,
                color: 'red'
              }
            ]
          },
          {
            qr: card.activationCode,
            fit: cmToPt(3),
            absolutePosition: {
              x: x + cmToPt(0.5),
              y: y + cmToPt(1)
            }
          },
          {
            text: this.formatActivationCode(card.activationCode),
            fontSize: 12,
            bold: true,
            absolutePosition: {
              x: x + cmToPt(0.5),
              y: y + cmToPt(4.2)
            },
            font: 'OpenSans'
          },
          {
            image: writeRotatedText(card.serialNumber),
            fit: [cmToPt(2), cmToPt(3.5)],//[200, 80],
            alignment: 'left',
            absolutePosition: {
              x: x + cardWidth - cmToPt(1),
              y: y + cmToPt(0.8)
            }
          }
        ]
      };

      content.push(cardBlock);

      //Add page break after every 10 cards
      if ((index + 1) % 10 === 0 && index !== cards.length - 1) {
        content.push({ text: '', pageBreak: 'after' });
      }
    });

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [0, 0, 0, 0],
      content
    }
    return pdfMake.createPdf(docDefinition);
  }

  download() {
    const generatedPdf = this.generatePdf(this.data);
    generatedPdf.download(OUT_FILE_NAME);
    this.dhhpAlert.dhhpAlertConfirmation('Download started. Please check your browser for progress and the \'Downloads\' folder once complete.', 'Cancel');
    this.savedActivationCodes = true;
  }

  print() {
    const generatedPdf = this.generatePdf(this.data);
    generatedPdf.print();
    this.savedActivationCodes = true;
  }

  finish() {
    if (this.isDataAvailable && !this.savedActivationCodes) {
      this.openConfirmDialog();
      return;
    }
    this.navigateToBulkUploadScreen();
  }

  private navigateToBulkUploadScreen() {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  openConfirmDialog() {
    let dialogRef = this.dialog.open(BulkActivationConfirmComponent, {
      panelClass: 'custom-confirm-dialog'
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res == true) {
        this.navigateToBulkUploadScreen();
      }
    })
  }
}

export const writeRotatedText = text => {
  let ctx,
    canvas = document.createElement('canvas');
  canvas.width = 50;
  canvas.height = 350;

  ctx = canvas.getContext('2d');
  ctx.font = '29pt "OpenSans-Bold"';
  ctx.save();
  ctx.translate(50, 350);
  ctx.rotate(-0.5 * Math.PI);
  ctx.fillStyle = '#000';
  ctx.fillText(text, 0, 0);
  ctx.restore();
  return canvas.toDataURL();
};

